// HomePage.jsx
// Location: src/pages/HomePage.jsx
// Main dashboard with session start buttons, today's routine, and current/goal songs

import React from 'react';
import { Clock, Zap, RefreshCw, Music, Target } from 'lucide-react';
import { getCurrentSong, getGoalSong } from '../data/DrumData.v2';
import GradientHeading from '../components/GradientHeading';

/**
 * Home Page Component
 * Displays:
 * - Session start buttons (20min full, 10min quick)
 * - Today's routine breakdown
 * - Current and goal songs
 * 
 * @param {Object} routine - Today's routine { warmup, groove, song }
 * @param {Function} onGenerateRoutine - Callback to regenerate routine
 * @param {Function} onStartSession - Callback when starting session (type: '20min' | '10min')
 * @param {Array} songs - Array of all songs
 * @param {Function} onMarkComplete - Callback to mark current song as complete
 * @param {Number} routineVersion - Version number to force re-renders
 */
const HomePage = ({ 
  routine, 
  onGenerateRoutine, 
  onStartSession, 
  songs, 
  onMarkComplete,
  routineVersion
}) => {
  // Get current and goal songs
  const currentSong = getCurrentSong(songs);
  const goalSong = getGoalSong(songs);

  // Show loading state if routine not ready
  if (!routine) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center py-20">
          <div className="animate-pulse text-gray-400">Loading your routine...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <GradientHeading size="hero" className="mb-4">
          Find your rhythm
        </GradientHeading>
        <p className="text-gray-600 text-lg">
          Master the drums with practice sessions designed just for you
        </p>
      </div>
      
      {/* Session Start Buttons */}
      <div className="mb-12 space-y-4">
        {/* 20-Minute Full Practice */}
        <button
          onClick={() => onStartSession('20min')}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-3xl p-8 transition-all shadow-lg hover:shadow-xl"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-2xl font-bold mb-2">Start Your Session</div>
              <div className="text-blue-100">Warmup • Skills • Song</div>
              <div className="mt-4 flex items-center gap-2 text-sm text-blue-100">
                <Clock className="w-4 h-4" />
                <span>Full structured practice</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-6xl font-bold">20</div>
              <div className="text-blue-100">minutes</div>
            </div>
          </div>
        </button>
        
        {/* 10-Minute Quick Session */}
        <button
          onClick={() => onStartSession('10min')}
          className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-3xl p-6 transition-all shadow-lg hover:shadow-xl"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <div className="text-xl font-bold mb-1">Quick Session</div>
              <div className="text-purple-100 text-sm">Warmup • Song</div>
            </div>
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6" />
              <div className="text-3xl font-bold">10 min</div>
            </div>
          </div>
        </button>
      </div>
      
      {/* Today's Routine Card - KEY PROP FORCES RE-RENDER */}
      <div 
        key={routineVersion}
        className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">Today's Routine</h2>
          <button
            onClick={() => {
              console.log('🖱️ Regenerate button clicked!');
              onGenerateRoutine();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-all"
            title="Generate a new routine"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Step 1: Warmup */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex-shrink-0">
              1
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase font-medium mb-1">
                Warmup
              </div>
              <div className="font-semibold text-gray-800">
                {routine.warmup.title.split('—')[0].trim()}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>{routine.warmup.duration}</span>
            </div>
          </div>
          
          {/* Step 2: Groove/Skills */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex-shrink-0">
              2
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase font-medium mb-1">
                Skills
              </div>
              <div className="font-semibold text-gray-800">
                {routine.groove.title}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>{routine.groove.duration}</span>
            </div>
          </div>
          
          {/* Step 3: Song */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex-shrink-0">
              3
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase font-medium mb-1">
                Song
              </div>
              <div className="font-semibold text-gray-800">
                {routine.song.title}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>8 min</span>
            </div>
          </div>
          
          {/* Total Duration */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <span className="text-gray-600 font-medium">Total</span>
            <span className="text-2xl font-bold text-purple-600">20 min</span>
          </div>
        </div>
      </div>
      
      {/* Current & Goal Songs Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Song Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Music className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-500 uppercase">
              Current
            </span>
          </div>
          {currentSong ? (
            <div>
              <div className="font-semibold text-gray-800 mb-2">
                {currentSong.title}
              </div>
              <div className="text-sm text-gray-500 mb-4">
                {currentSong.difficulty} • {currentSong.tempo}
              </div>
              <button
                onClick={() => onMarkComplete(currentSong.id)}
                className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-medium transition-all"
              >
                Mark Complete ✓
              </button>
            </div>
          ) : (
            <div className="text-gray-400">No song selected</div>
          )}
        </div>
        
        {/* Goal Song Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-orange-500" />
            <span className="text-sm font-medium text-gray-500 uppercase">
              Goal
            </span>
          </div>
          {goalSong ? (
            <div>
              <div className="font-semibold text-gray-800 mb-2">
                {goalSong.title}
              </div>
              <div className="text-sm text-gray-500">
                {goalSong.difficulty} • {goalSong.tempo}
              </div>
            </div>
          ) : (
            <div className="text-gray-400">No goal set</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;